import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter, Inject } from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { Subscription, firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { SessionsService } from '../../services/sessions/sessions.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { NameService } from '../../services/name/name.service';
import { DolbyService } from '../../services/dolby/dolby.service';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { UserService } from '../../services/user/user.service';
import { defaultPopoverOptions } from '../../services/theme/theme.service';
import { EquipmentService } from '../../services/equipment/equipment.service';
import { RecordingService } from '../../services/recording/recording.service';
import { RolesService } from '../../services/roles/roles.service';
import { RoleLimits, Roles } from '@sc/types';
import { SupportCenterService } from '../../services/support-center/support-center.service';
import { VideoProcessorType } from '@sc/types';
import { DeviceDetectorService } from 'ngx-device-detector';
import { ConfTypes } from '@sc/types';
import { DolbyInfoMenuComponent } from '../../menus/dolby-info-menu/dolby-info-menu.component';
import { StudioSidebarService } from '../../services/studio-sidebar/studio-sidebar.service';
import { WalletService } from '../../services/wallet/wallet.service';
import { WindowToken } from '../../services/window/window';
import { DailyService } from '../../services/daily/daily.service';

@Component({
  selector: 'sc-name-menu',
  templateUrl: './name-menu.component.html',
  styleUrls: ['./name-menu.component.scss'],
})
export class NameMenuComponent implements OnInit, OnDestroy {
  @Output() closeSidebar = new EventEmitter<boolean>();
  @ViewChild('imageFile') imageFile: ElementRef;
  user$ = this.userService.activeUser$;
  session$ = this.sessionsService.studioSession$;
  localParticipant$ = this.dolbyService.localParticipant$;
  recording$ = this.recordingService.recording$;
  isVoiceConf = this.dolbyService.conferenceParams.dolbyVoice;
  videoProcessor$ = this.dolbyService.videoProcessor$;
  dolbyConversation$ = this.dolbyService.dolbyConversation$;
  dailyCall$ = this.dailyService.dailyCall$;

  plan$ = this.walletService.studioPlan$;
  role$ = this.rolesService.role$;
  roles = Roles;
  roleLimits = RoleLimits;
  ConfTypes = ConfTypes;

  name: string;
  hideIncomingVideo: boolean;
  hideSelf: boolean;
  echoCancellation: boolean;
  recordingQuality: boolean;
  participantOnWindows = true;
  windows4KOverride: boolean;
  allowMultipleSidebars = this.studioSidebarService.allowMultipleSidebars$.value;
  noiseReduction: boolean;
  sessionEchoCancellation: boolean;
  backstage = false;
  noWASM = false;
  videoFilters = [
    {
      name: 'None',
      filter: VideoProcessorType.None,
    },
    {
      name: 'Blur',
      filter: VideoProcessorType.Bokeh,
    },
    {
      name: 'Image',
      filter: VideoProcessorType.BackgroundReplacement,
    },
  ];
  currentVideoFilter = this.videoFilters[0];

  participantMap = new Map<string, { os: string }>();

  subs: Array<Subscription> = [];

  constructor(
    private deviceDetectorService: DeviceDetectorService,
    private popoverController: PopoverController,
    private toastrService: ToastrService,
    private sessionsService: SessionsService,
    private userService: UserService,
    private analyticsService: AnalyticsService,
    private equipmentService: EquipmentService,
    private recordingService: RecordingService,
    private rolesService: RolesService,
    private nameService: NameService,
    private supportCenterService: SupportCenterService,
    private dolbyService: DolbyService,
    private dailyService: DailyService,
    private studioSidebarService: StudioSidebarService,
    private walletService: WalletService,
    @Inject(WindowToken) public window: Window
  ) {}

  ngOnInit() {
    this.setupLocation();
    this.setupName();
    this.setupHideIncomingVideo();
    this.setupHideSelf();
    this.setupEchoCancellation();
    this.setupNoiseReduction();
    this.setupSessionEchoCancellation();
    this.setupSessionRecordingQuality();
    this.setupBackgroundBlur();
    this.setupParticipants();
    this.setupWindows4KOverride();
    const browser = this.deviceDetectorService.getDeviceInfo().browser;
    this.noWASM = browser === 'Firefox' || browser === 'Safari';
  }

  async setVoiceConf(type: ConfTypes) {
    if (this.recording$.value) {
      return;
    }
    await this.sessionsService.setSessionConfType(this.session$.value.sessionID, type);
    this.analyticsService.track('switched conf type in session', { isVoiceConf: type === ConfTypes.VOICE });
    this.isVoiceConf = type === ConfTypes.VOICE;
  }

  async selectVideoFilter(event) {
    const filter = event.detail.value.filter as VideoProcessorType;
    if (filter === this.currentVideoFilter.filter) return;
    this.currentVideoFilter = this.videoFilters.find((f) => f.filter === filter);
    switch (filter) {
      case VideoProcessorType.None:
      case VideoProcessorType.Bokeh:
        this.dolbyService.setVideoProcessing(filter);
        break;
      case VideoProcessorType.BackgroundReplacement:
        this.imageFile.nativeElement.click();
        break;
    }
    await this.analyticsService.track('set video filter', { filter });
  }

  imageFileInput(event) {
    const file = event.target.files[0];
    const image = document.createElement('img');
    image.style.display = 'none';
    const reader = new FileReader();
    reader.onload = (e) => {
      image.src = e.target.result as string;
      document.body.appendChild(image);
      setTimeout(() => {
        this.dolbyService.setVideoProcessing(VideoProcessorType.BackgroundReplacement, image);
        document.body.removeChild(image);
      }, 0);
    };
    reader.readAsDataURL(file);
  }

  setupParticipants() {
    if (this.dolbyConversation$.value) {
      const dolbySub = this.dolbyService.activeParticipants$.subscribe(async (confParticipants) => {
        if (!confParticipants) return;
        const equipmentPromises = [];
        confParticipants.participants.forEach((participant) => {
          if (!this.participantMap.has(participant.info.externalId)) {
            equipmentPromises.push(
              firstValueFrom(this.sessionsService.getParticipantEquipmentSnapshots(participant.info.externalId))
            );
          }
        });
        this.participantMap.forEach((value, key) => {
          if (!confParticipants.participants.some((participant) => participant.info.externalId === key)) {
            this.participantMap.delete(key);
          }
        });
        await Promise.all(equipmentPromises).then((equipmentSnapshots) => {
          equipmentSnapshots.forEach((equipmentSnapshot) => {
            const equipment = equipmentSnapshot.data();
            const uid = equipmentSnapshot.ref.parent.parent.id;
            this.participantMap.set(uid, { os: equipment.deviceInfo.os });
          });
        });

        this.participantOnWindows = Array.from(this.participantMap.values()).some(
          (participant) => participant.os === 'Windows'
        );
      });
      this.subs.push(dolbySub);
    }

    if (this.dailyCall$.value) {
      const dailySub = this.dailyService.stageParticipants$.subscribe(async (participants) => {
        if (!participants) return;
        const equipmentPromises = [];
        participants.forEach((participant, key) => {
          if (!this.participantMap.has(key)) {
            equipmentPromises.push(firstValueFrom(this.sessionsService.getParticipantEquipmentSnapshots(key)));
          }
        });
        this.participantMap.forEach((value, key) => {
          if (!participants.has(key)) {
            this.participantMap.delete(key);
          }
        });
        await Promise.all(equipmentPromises).then((equipmentSnapshots) => {
          equipmentSnapshots.forEach((equipmentSnapshot) => {
            const equipment = equipmentSnapshot.data();
            if (equipment) {
              const uid = equipmentSnapshot.ref.parent.parent.id;
              this.participantMap.set(uid, { os: equipment.deviceInfo.os });
            }
          });
        });

        this.participantOnWindows = Array.from(this.participantMap.values()).some(
          (participant) => participant.os === 'Windows'
        );
      });
      this.subs.push(dailySub);
    }
  }

  setupBackgroundBlur() {
    this.videoProcessor$.subscribe((processor) => {
      setTimeout(() => {
        this.currentVideoFilter = this.videoFilters.find((filter) => filter.filter === processor.type);
      }, 0);
    });
  }

  setupLocation() {
    const sub = this.dolbyService.location$.subscribe((location) => {
      this.backstage = location === 'backstage';
    });
    this.subs.push(sub);
  }

  async setupName() {
    const sub = this.nameService
      .getName(this.session$.value.sessionID, this.user$.value.uid)
      .subscribe((name: string) => {
        this.name = name;
      });
    this.subs.push(sub);
  }

  async saveName() {
    try {
      if (!this.name) this.name = await this.nameService.getRandomName(4);
      await this.nameService.setName(this.session$.value.sessionID, this.user$.value.uid, this.name);
      await this.popoverController.dismiss();
      this.toastrService.success(this.name, `Successfully changed your Name`, {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: false,
        timeOut: 5 * 1000,
        toastComponent: GeneralToastComponent,
      });
      await this.analyticsService.track('edited name', { success: true });
    } catch (error) {
      await this.popoverController.dismiss();
      this.toastrService.error(this.name, `Failed to change your Name`, {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: false,
        timeOut: 5 * 1000,
        toastComponent: GeneralToastComponent,
      });
      await this.analyticsService.track('edited name', { success: false });
    }
  }

  async toggleNoiseReduction(toggle: ICustomEvent) {
    this.dolbyService.setLocalNoiseReduction(toggle.detail.checked ? 2 : 0);

    this.analyticsService.track('toggled dolby noise reduction', {
      showID: this.session$.value.showID,
      sessionID: this.session$.value.sessionID,
      participant: this.localParticipant$.value.info.externalId,
      enabled: this.noiseReduction,
    });
  }

  async openDolbyInfoMenu(click: MouseEvent) {
    const menu = await this.popoverController.create({
      ...defaultPopoverOptions,
      component: DolbyInfoMenuComponent,
      cssClass: 'info-menu',
      event: click,
    });
    await menu.present();
    await this.analyticsService.track('opened dolby voice info');
  }

  setupHideIncomingVideo() {
    const sub = this.dolbyService.hideIncomingVideo$.subscribe((hideIncomingVideo: boolean) => {
      this.hideIncomingVideo = hideIncomingVideo;
    });

    this.subs.push(sub);
  }

  setupHideSelf() {
    const sub = this.localParticipant$.subscribe((localParticipant) => {
      console.log(localParticipant);
      if (!localParticipant) return;
      this.hideSelf = localParticipant.minimized || false;
    });

    this.subs.push(sub);
  }

  setupNoiseReduction() {
    const sub = this.equipmentService.noiseReduction$.subscribe((noiseReduction) => {
      if (this.backstage) return;
      this.noiseReduction = noiseReduction;
    });

    this.subs.push(sub);
  }

  setupEchoCancellation() {
    const sub = this.equipmentService.echoCancellation$.subscribe((echoCancellation: boolean) => {
      this.echoCancellation = echoCancellation;
    });

    this.subs.push(sub);
  }
  async setupSessionEchoCancellation() {
    const sub = this.session$.subscribe((session) => {
      this.sessionEchoCancellation =
        session?.sessionEchoCancellation !== null && session?.sessionEchoCancellation !== undefined;
    });

    this.subs.push(sub);
  }

  async setupSessionRecordingQuality() {
    const sub = this.session$.subscribe((session) => {
      this.recordingQuality = session?.maxQuality;
    });

    this.subs.push(sub);
  }

  async toggleAllowMultipleSidebars(event: ICustomEvent) {
    this.studioSidebarService.toggleAllowMultipleSidebars(event.detail.checked);
    this.allowMultipleSidebars = event.detail.checked;
    await this.analyticsService.track('toggled allow multiple sidebars', { enabled: event.detail.checked });
  }

  resetToolbarLayout() {
    this.studioSidebarService.resetSidebarItems();
  }

  async toggleHideIncomingVideo(event: ICustomEvent) {
    await this.dolbyService.setHideIncomingVideo(event.detail.checked);
    await this.analyticsService.track('toggled hide incoming video', { enabled: event.detail.checked });
  }

  async toggleHideSelf(event: ICustomEvent) {
    if (this.dolbyConversation$.value) {
      this.dolbyService.minimizeLocalParticipant(event.detail.checked);
    } else if (this.dailyCall$.value) {
      this.dailyService.minimizeLocalParticipant(event.detail.checked);
    }

    if (event.detail.checked) {
      this.toastrService.success('You hid your own portrait (Others can still see you)', 'Successfully Hid Yourself', {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: false,
        timeOut: 5 * 1000,

        toastComponent: GeneralToastComponent,
      });
    }
    this.analyticsService.track('toggled hide self', { minimized: event.detail.checked });
  }

  toggleEchoCancellation(event: ICustomEvent) {
    this.equipmentService.setEchoCancellation(event.detail.checked);
    this.echoCancellation = event.detail.checked;
    this.toggleSessionEchoCancellation(null);
    this.analyticsService.track('toggled echo cancellation', { enabled: event.detail.checked });
  }

  toggleSessionEchoCancellation(event: ICustomEvent) {
    const active = event?.detail?.checked ?? this.sessionEchoCancellation;
    this.sessionsService.setSessionEchoCancellatioin(
      this.sessionsService.studioSessionID$.value,
      active ? this.echoCancellation : null
    );

    if (active) {
      this.analyticsService.track('set session echo cancellation', {
        sessionEchoCancellation: this.echoCancellation,
      });
    }
  }

  toggleSessionRecordingQuality(event: ICustomEvent) {
    this.sessionsService.setSessionRecordingQuality(this.sessionsService.studioSessionID$.value, event.detail.checked);
    this.recordingQuality = event.detail.checked;
    this.analyticsService.track('toggled recording quality', { enabled: event.detail.checked });
  }

  toggleWindows4KOverride(event: ICustomEvent) {
    this.sessionsService.setSessionWindows4KOverride(this.sessionsService.studioSessionID$.value, event.detail.checked);
  }

  setupWindows4KOverride() {
    this.session$.subscribe((session) => {
      this.windows4KOverride = session?.windows4KOverride;
    });
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
